import { Component, OnDestroy, OnInit, AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { cloneDeep } from 'lodash';
import { filter, ReplaySubject, switchMap, take, takeUntil } from 'rxjs';

import { AuthenticationState, coreSelectors } from 'app/core/state';
import { delayFor } from 'app/core/utilities/async-utils';
import { SurveyService } from 'app/modules/survey/services/survey.service';
import { Loggable } from 'app/utils/logging/loggable';
import SocialRoomSet from 'entity/SocialRoomSet';

import { ScootaversePageService } from '../services/scootaverse-page.service';
import { scootaversePageSelectors } from '../state';
import { homePageDescriptionText } from '../strings';

export const MOCK_ATTENDE_SUMMARY_ID = 'adb0c5e4-2d92-4893-bce4-040b0745dd74';

@Component({
  selector: 'scootaverses',
  templateUrl: './scootaverses.component.html',
  styleUrls: ['./scootaverses.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ScootaversesComponent extends Loggable implements OnInit, OnDestroy, AfterViewInit {
  protected roomSets$ = new ReplaySubject<SocialRoomSet[]>(1);
  protected loading$ = new ReplaySubject<boolean>(1);
  private destroyed$ = new ReplaySubject<boolean>(1);
  private authenticated = false;

  public constructor(
    private store: Store,
    private scootaversePageService: ScootaversePageService,
    private surveyService: SurveyService,
    private activatedRoute: ActivatedRoute,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    super();
    this.setDisplayName('ScootaversesComponent');
    this.store
      .select(scootaversePageSelectors.getScootaverses)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ data: rooms, loading }) => {
        const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
        const sortedRooms = rooms.slice().sort((a, b) => collator.compare(a.name || '', b.name || ''));
        this.roomSets$.next(cloneDeep(sortedRooms));
        this.loading$.next(loading);
      });
    this.store
      .select(coreSelectors.getAuthenticationState)
      .pipe(filter((state) => state === AuthenticationState.AUTHENTICATED))
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.authenticated = true;
          this.changeDetectorRef.markForCheck();
        },
      });
  }

  public ngOnInit(): void {
    this.scootaversePageService.fetchScootaverses();
    this.checkSurvey();

    this.activatedRoute.data.pipe(takeUntil(this.destroyed$)).subscribe((data) => {
      if (data.guestSurvey) {
        this.checkSurvey();
      }
    });
  }

  public async ngAfterViewInit(): Promise<void> {
    // TODO: Remove this when we have a better solution for race condition
    await delayFor(2000);
    this.roomSets$.pipe(take(1)).subscribe((rooms) => {
      if (rooms.length === 0 && this.authenticated) {
        this.refetchScootaverses();
      }
    });
  }

  public refetchScootaverses(): void {
    this.scootaversePageService.fetchScootaverses();
  }

  public onScroll(): void {
    this.scootaversePageService.fetchMoreScootaverses();
  }

  public get homePageDescriptionText(): string {
    return homePageDescriptionText();
  }

  private checkSurvey(): void {
    this.activatedRoute.data
      .pipe(
        filter((v) => v && v.show),
        switchMap(() => this.activatedRoute.params),
        filter((params) => params && params['room-id']),
        switchMap((params) =>
          this.surveyService
            .getSurvey(2)
            .pipe(switchMap((survey) => this.surveyService.open({ survey, id: params['room-id'] }))),
        ),
        takeUntil(this.destroyed$),
      )
      .subscribe();
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
